import React, { useEffect, useRef, useState } from 'react';
import ModalHeaderSimple from '../../presentations/ModalHeaderSimple';
import styled from 'styled-components';
import { fontSize } from '../utility/Mixin';
import Button from '../../presentations/Button';
import { AppState } from '../../stores';
import { useSelector } from 'react-redux';
import AlertMessage, { AlertMessageInterface } from '../../presentations/AlertMessage';
import { api } from '../../services/api';

interface RoomShareInterface {
  onClose?: () => void;
}

const RoomShare: React.FC<RoomShareInterface> = (props) => {
  const data = useSelector((state: AppState) => state.roomShareModal.data);
  const linkRef = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState<AlertMessageInterface>();
  const [shareLink, setShareLink] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!data?.roomId || !data?.title) {
      setShareLink('');
      return;
    }

    let cancelled = false;

    const buildBaseLink = (inviteToken?: string | null): string => {
      const params = new URLSearchParams({
        title: data.title,
        roomId: data.roomId,
      });
      if (inviteToken) {
        params.set('invite', inviteToken);
      }
      return `${window.location.protocol}//${window.location.host}/room?${params.toString()}`;
    };

    const loadShareLink = async (): Promise<void> => {
      setLoading(true);
      setMessage(undefined);

      try {
        const { data: invite } = await api.post<{
          enabled: boolean;
          inviteToken: string | null;
          expiresAt: number | null;
        }>(`/room/${data.roomId}/guest-invite`);

        if (cancelled) {
          return;
        }

        setShareLink(buildBaseLink(invite.enabled ? invite.inviteToken : null));
      } catch {
        if (cancelled) {
          return;
        }
        // JWT 無効時や一時障害でも title+roomId リンクは出せる
        setShareLink(buildBaseLink(null));
        setMessage({
          level: 'error',
          body: '招待トークンの取得に失敗しました。ログイン済みユーザー向けのリンクのみ表示しています。',
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadShareLink();

    return () => {
      cancelled = true;
    };
  }, [data?.roomId, data?.title]);

  const handleCopyLink = () => {
    if (!linkRef.current || !shareLink) {
      return;
    }
    linkRef.current.select();
    document.execCommand('copy');
    setMessage({
      level: 'success',
      body: 'リンクをコピーしました',
    });
  };

  return (
    <>
      <ModalHeaderSimple>Meetingの共有</ModalHeaderSimple>
      <Body>
        <AlertMessage {...message} />
        <LinkCopy>
          <LinkCopyText
            type="text"
            value={loading ? 'リンクを準備しています…' : shareLink}
            readOnly
            ref={linkRef}
          />
          <LinkCopyButton type="button" onClick={handleCopyLink} disabled={loading || !shareLink}>
            リンクをコピー
          </LinkCopyButton>
        </LinkCopy>
        <Sub>このリンクからMeetingに参加することができます（ゲストは招待トークン付きリンクが必要です）。</Sub>
      </Body>
      <Action>
        <Button type={'button'} color={'secondary'} onClick={props.onClose}>
          閉じる
        </Button>
      </Action>
    </>
  );
};

export const Body = styled.div`
  padding: 0 20px;
`;

export const LinkCopy = styled.div`
  display: flex;
`;

export const LinkCopyText = styled.input`
  flex: 1;
  ${fontSize(18)};
  font-weight: 700;
  line-height: 1.3;
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 6px;
`;

export const LinkCopyButton = styled.button`
  margin-left: 15px;
  color: #4390d9;
  text-decoration: underline;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Sub = styled.small`
  margin-top: 10px;
  display: block;
  ${fontSize(12)};
  font-weight: 400;
`;

export const Action = styled.div`
  margin-top: 25px;
  padding: 0 20px 20px;
  display: flex;
  justify-content: flex-end;
`;

export const LinkCopied = styled.div``;

export default RoomShare;
